import React, { RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  SCBottomBarContainer,
  SCButtonWrapper,
  SCButtonCircle,
  SCButtonLabel,
  SCButtonsRow,
  SCButtonIcon,
  SCPulseRing,
} from 'components/AugmentedReality/Experience/BottomBar/styled'
import ModalInfos from 'components/AugmentedReality/Structure/ModalInfos'
import { AugmentedRealityType } from '../../../../types/augmentedReality'
import useCamera from 'components/AugmentedReality/hooks/useCamera'
import ConfirmModal from 'components/AugmentedReality/Structure/ConfirmModal'
import { SCDownloadToast, SCDownloadToastEmoji } from 'components/AugmentedReality/Experience/styled'

interface BottomBarProps {
  ra: AugmentedRealityType;
  currentOverlay: string;
  camera: ReturnType<typeof useCamera>;
  videoRef: RefObject<HTMLVideoElement | null>;
  download: boolean;
  onDownload: (state: boolean) => void;
  onPrevious: () => void;
  onCancel: () => void;
  isPaused: boolean;
  onPaused: (pause: boolean) => void;
  snapshot: string | null;
  setSnapshot: (img: string|null) => void;
  onPhoto: () => void;
}

const BottomBar = ({ ra, currentOverlay, download, onDownload, onPrevious, onCancel, camera, videoRef, isPaused, onPaused, snapshot, setSnapshot, onPhoto }: BottomBarProps) => {
  const { t } = useTranslation()
  const [openInfo, setOpenInfo] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showDownloadToast, setShowDownloadToast] = useState(false);
  const [animateInfo, setAnimateInfo] = useState(false);
  const [animatePauseBtn, setAnimatePauseBtn] = useState(false);
  const [animateExit, setAnimateExit] = useState(false);

  const triggerAnimate = (setter: (v: boolean) => void) => {
    setter(true);
    setTimeout(() => setter(false), 750);
  };

  const generateImageWithOverlay = async (baseImage: string, overlaySrc: string, flipHorizontal: boolean = false): Promise<string> => {
    return new Promise((resolve) => {
      const baseImg = new Image();
      const overlayImg = new Image();

      overlayImg.crossOrigin = 'anonymous';

      baseImg.onload = () => {
        overlayImg.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          canvas.width = baseImg.width;
          canvas.height = baseImg.height;

          if (flipHorizontal) {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
          }
          ctx.drawImage(baseImg, 0, 0);
          if (flipHorizontal) {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
          }

          const maxOverlayWidth = canvas.width * 0.8;
          const maxOverlayHeight = canvas.height * 0.5;
          const ratio = overlayImg.width / overlayImg.height;
          let overlayW = maxOverlayWidth;
          let overlayH = overlayW / ratio;
          if (overlayH > maxOverlayHeight) {
            overlayH = maxOverlayHeight;
            overlayW = overlayH * ratio;
          }

          const x = (canvas.width - overlayW) / 2;
          const y = (canvas.height - overlayH) / 2;
          ctx.drawImage(overlayImg, x, y, overlayW, overlayH);
          resolve(canvas.toDataURL('image/png'));
        };

        overlayImg.src = overlaySrc;
      };

      baseImg.src = baseImage;
    });
  };

  const handleDownload = async () => {
    let baseImage: string | null = snapshot;

    if (!isPaused) {
      baseImage = await camera.capturePhoto(videoRef.current, false);
    }

    if (baseImage) {
      const result = await generateImageWithOverlay(baseImage, currentOverlay, camera.facingMode === 'user');
      const now = new Date();
      const filename = `capture-${now.toISOString()}.png`;

      const link = document.createElement('a');
      link.href = result;
      link.download = filename;
      link.click();

      setShowDownloadToast(true);
      setTimeout(() => setShowDownloadToast(false), 2000);
    }
  };

  return (
    <>
      <SCBottomBarContainer>
        <SCButtonsRow>
          <SCButtonWrapper onClick={onPrevious}>
            <SCButtonCircle $size="normal">
              <SCButtonIcon src={'/static/ra/back2.svg'} alt="Retour" />
            </SCButtonCircle>
            <SCButtonLabel $isCenter={false}>{t('augmentedReality.back')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              triggerAnimate(setAnimateInfo)
              setOpenInfo(true)
            }}
          >
            <SCButtonCircle $size="normal" className={animateInfo ? 'animate' : ''}>
              <SCButtonIcon src={'/static/ra/info-small.svg'} alt="Infos" />
            </SCButtonCircle>
            <SCButtonLabel $isCenter={false}>{t('augmentedReality.info')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={async () => {
              setAnimatePauseBtn(true)
              setTimeout(() => setAnimatePauseBtn(false), 750)
              if (!isPaused) {
                const snapshot = await camera.capturePhoto(videoRef.current)
                camera.stopCamera()
                if (snapshot) {
                  setSnapshot(snapshot)
                  onPaused(true)
                }
              } else {
                await camera.startCamera()
                onPaused(false)
                setSnapshot(null)
              }
            }}
          >
            <SCPulseRing>
              <SCButtonCircle $size="xlarge" $disableBubble className={animatePauseBtn ? 'animate' : ''}>
                {isPaused ? (
                  <SCButtonIcon src={'/static/ra/play.svg'} alt="Play" $size="xlarge" $type="play" />
                ) : (
                  <SCButtonIcon src={'/static/ra/pause.svg'} alt="Pause" $size="xlarge" />
                )}
              </SCButtonCircle>
            </SCPulseRing>
            <SCButtonLabel $isCenter={true}>
              {isPaused ? t('augmentedReality.experience.live') : t('augmentedReality.experience.freeze')}
            </SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper onClick={onPhoto}>
            <SCButtonCircle $size="normal">
              <SCButtonIcon src={'/static/ra/photo.png'} alt="Photo" />
            </SCButtonCircle>
            <SCButtonLabel $isCenter={false}>{t('augmentedReality.experience.photo')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              triggerAnimate(setAnimateExit)
              setShowExitConfirm(true)
            }}
          >
            <SCButtonCircle $size="normal" className={animateExit ? 'animate' : ''}>
              <SCButtonIcon src={'/static/ra/exit.svg'} alt="Télécharger" />
            </SCButtonCircle>
            <SCButtonLabel $isCenter={false}>{t('augmentedReality.experience.exit')}</SCButtonLabel>
          </SCButtonWrapper>
        </SCButtonsRow>
      </SCBottomBarContainer>

      {openInfo && (
        <ModalInfos
          title={ra.config.infosTitle}
          text={ra.config.infos}
          video={ra.config.infosVideo}
          onClose={() => {
            setOpenInfo(false)
          }}
        />
      )}

      {showDownloadConfirm && (
        <ConfirmModal
          title={t('augmentedReality.experience.downloadPhoto')}
          description={t('augmentedReality.experience.downloadConfirmDesc')}
          onCancel={() => setShowDownloadConfirm(false)}
          onConfirm={() => {
            onDownload(true)
            setShowDownloadConfirm(false)
            handleDownload()
          }}
          labelCancel={t('augmentedReality.experience.iRefuse')}
          labelConfirm={t('augmentedReality.experience.iAccept')}
        />
      )}

      {showExitConfirm && (
        <ConfirmModal
          title={t('augmentedReality.experience.exitTitle')}
          description={t('augmentedReality.experience.exitDesc')}
          onCancel={() => {
            camera.stopCamera()
            onCancel()
          }}
          onConfirm={() => {
            setShowExitConfirm(false)
          }}
          labelCancel={t('augmentedReality.experience.takeBreak')}
          labelConfirm={t('augmentedReality.experience.continueExperience')}
        />
      )}

      {showDownloadToast && (
        <SCDownloadToast>
          <SCDownloadToastEmoji src={'/static/ra/yeux-etoiles.svg'} />
          {t('augmentedReality.experience.downloadToast')}
        </SCDownloadToast>
      )}
    </>
  )
}

export default BottomBar